import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { getPointOffers, redeemPointOffer } from '../api/pointOffers';
import type { CustomerBalance, PointOffer } from '../types/pointOffers';

const gradientBackground = 'linear-gradient(120deg, #b8957b 0%, #a98c74 100%)';

const PointOffers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [offers, setOffers] = useState<PointOffer[]>([]);
  const [balance, setBalance] = useState<CustomerBalance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [redeemingId, setRedeemingId] = useState<number | null>(null);
  const [redeemError, setRedeemError] = useState<string | null>(null);

  const loadOffers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPointOffers();
      setOffers(response.offers);
      setBalance(response.customerBalance);
    } catch (err) {
      console.error('Failed to load point offers', err);
      setError(t('pointOffers.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const availablePoints = useMemo(
    () => balance?.availablePoints ?? 0,
    [balance]
  );

  const renderOfferBadge = (offer: PointOffer) => {
    if (offer.type === 'percentage') {
      return t('pointOffers.labels.percentage', { value: offer.value });
    }

    if (offer.type === 'fixed_amount') {
      return t('pointOffers.labels.amount', { value: offer.value });
    }

    return offer.name || offer.code;
  };

  const handleRedeem = async (offerId: number) => {
    if (redeemingId) {
      return;
    }

    try {
      setRedeemingId(offerId);
      setRedeemError(null);
      await redeemPointOffer(offerId);
      await loadOffers();
    } catch (err) {
      console.error('Failed to redeem point offer', err);
      setRedeemError(t('pointOffers.error.redeemFailed'));
    } finally {
      setRedeemingId(null);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 0 }}>
        <Stack spacing={2} alignItems="center">
          <Typography color="error" variant="h6">
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate(-1)}>
            {t('pointOffers.actions.goBack')}
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container sx={{ py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t('pointOffers.actions.goBack')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 700 }}>
            {t('pointOffers.title')}
          </Typography>
        </Stack>

        <Typography sx={{ color: '#6b7280', mb: 3 }}>
          {t('pointOffers.description')}
        </Typography>

        {redeemError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setRedeemError(null)}
          >
            {redeemError}
          </Alert>
        )}

        <Box
          sx={{
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            color: '#ffffff',
            background: gradientBackground,
          }}
        >
          <Stack spacing={1}>
            <Typography sx={{ opacity: 0.9, fontWeight: 600 }}>
              {t('pointOffers.balance.title')}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>
              {availablePoints}{' '}
              <Typography
                component="span"
                sx={{ fontSize: 20, fontWeight: 500, opacity: 0.8 }}
              >
                {t('pointOffers.balance.points')}
              </Typography>
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>
              {t('pointOffers.balance.summary', {
                earned: balance?.totalEarned ?? 0,
                redeemed: balance?.totalRedeemed ?? 0,
              })}
            </Typography>
          </Stack>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {offers.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  borderRadius: 3,
                  border: '1px dashed #d1d5db',
                  bgcolor: '#ffffff',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: '#6b7280' }}>
                  {t('pointOffers.messages.noOffers')}
                </Typography>
              </Box>
            </Grid>
          )}
          {offers.map((offer) => {
            const { customerEligibility } = offer;
            const pointsNeeded = Math.max(offer.pointsCost - availablePoints, 0);
            const canAfford = customerEligibility?.canAfford ?? false;
            const isRedeemed =
              customerEligibility?.hasReachedLimit ||
              customerEligibility?.alreadyRedeemed;

            const actionLabel = (() => {
              if (redeemingId === offer.id) {
                return t('pointOffers.actions.redeeming');
              }

              if (isRedeemed) {
                return t('pointOffers.actions.redeemed');
              }

              if (!canAfford) {
                return t('pointOffers.actions.notEnoughPoints');
              }

              return t('pointOffers.actions.redeem');
            })();

            return (
              <Grid size={{ xs: 12, md: 4, sm: 6 }} key={offer.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #e5e7eb',
                    height: '100%',
                    display: 'flex',
                    minHeight: '200px',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box
                          sx={{
                            bgcolor: '#EEF2FF',
                            color: '#4F46E5',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                        >
                          {renderOfferBadge(offer)}
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <img
                            src="/icons/ic_app_point.svg"
                            alt={t('pointOffers.balance.pointsIconAlt')}
                            width={20}
                            height={20}
                          />
                          <Typography sx={{ fontWeight: 700, color: '#111827' }}>
                            {offer.pointsCost}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack spacing={0.5}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {offer.name || offer.code}
                        </Typography>
                        {offer.description && (
                          <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
                            {offer.description}
                          </Typography>
                        )}
                      </Stack>

                      {!canAfford && !isRedeemed && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            bgcolor: '#FEF3C7',
                            color: '#B45309',
                            borderRadius: 2,
                            px: 1.5,
                            py: 1,
                          }}
                        >
                          <LockOutlinedIcon sx={{ fontSize: 18 }} />
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                            {t('pointOffers.messages.needMorePoints', {
                              count: pointsNeeded,
                            })}
                          </Typography>
                        </Stack>
                      )}

                      {isRedeemed && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            bgcolor: '#ECFDF5',
                            color: '#047857',
                            borderRadius: 2,
                            px: 1.5,
                            py: 1,
                          }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                            {t('pointOffers.messages.alreadyRedeemed')}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>

                  <Box sx={{ px: 2.5, pb: 2.5 }}>
                    <Button
                      variant={
                        canAfford && !isRedeemed ? 'contained' : 'outlined'
                      }
                      fullWidth
                      disabled={
                        !canAfford || isRedeemed || redeemingId === offer.id
                      }
                      onClick={() => handleRedeem(offer.id)}
                      sx={{
                        bgcolor: canAfford && !isRedeemed ? '#b8957b' : 'transparent',
                        color: canAfford && !isRedeemed ? 'white' : '#b8957b',
                        borderColor: '#b8957b',
                        '&:hover': {
                          bgcolor: canAfford && !isRedeemed ? '#a98c74' : 'rgba(184, 149, 123, 0.1)',
                        },
                        '&:disabled': {
                          bgcolor: '#e9ecef',
                          color: '#6c757d',
                          borderColor: '#e9ecef',
                        },
                      }}
                    >
                      {actionLabel}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/my-coupons')}
            sx={{ borderRadius: 999 }}
          >
            {t('pointOffers.actions.viewRedeemed')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PointOffers;
